import {
  keepPreviousData,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import {
  getDashboardActivity,
  getModulePreferences,
  getRecentItems,
  markModuleOpened,
  searchDashboard,
  setModuleFavorite,
} from "../Api/dashboard";
import {
  DashboardModuleKey,
  ModulePreferencesResponse,
} from "../Models/dashboard";

const MODULE_PREFERENCES_KEY = ["dashboardModulePreferences"];

export const SEARCH_MIN_LENGTH = 2;

export const useModulePreferences = () => {
  return useQuery({
    queryKey: MODULE_PREFERENCES_KEY,
    queryFn: getModulePreferences,
    select: (data) => data.modules,
  });
};

export const useToggleModuleFavorite = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      moduleKey,
      isFavorite,
    }: {
      moduleKey: DashboardModuleKey;
      isFavorite: boolean;
    }) => setModuleFavorite(moduleKey, isFavorite),
    // The star flips at once; a failed request puts it back.
    onMutate: async ({ moduleKey, isFavorite }) => {
      await queryClient.cancelQueries({ queryKey: MODULE_PREFERENCES_KEY });
      const previous = queryClient.getQueryData<ModulePreferencesResponse>(
        MODULE_PREFERENCES_KEY,
      );

      queryClient.setQueryData<ModulePreferencesResponse>(
        MODULE_PREFERENCES_KEY,
        (current) => {
          const modules = current?.modules ?? [];
          const exists = modules.some((m) => m.module_key === moduleKey);
          return {
            modules: exists
              ? modules.map((m) =>
                  m.module_key === moduleKey
                    ? { ...m, is_favorite: isFavorite }
                    : m,
                )
              : [
                  ...modules,
                  {
                    module_key: moduleKey,
                    is_favorite: isFavorite,
                    last_opened_at: null,
                  },
                ],
          };
        },
      );

      return { previous };
    },
    onError: (_error, _variables, context) => {
      if (context?.previous) {
        queryClient.setQueryData(MODULE_PREFERENCES_KEY, context.previous);
      }
    },
    onSuccess: (data) => {
      queryClient.setQueryData(MODULE_PREFERENCES_KEY, data);
    },
  });
};

export const useMarkModuleOpened = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (moduleKey: DashboardModuleKey) => markModuleOpened(moduleKey),
    onSuccess: (data) => {
      queryClient.setQueryData(MODULE_PREFERENCES_KEY, data);
    },
  });
};

export const useDashboardSearch = (query: string) => {
  const trimmed = query.trim();

  return useQuery({
    queryKey: ["dashboardSearch", trimmed],
    queryFn: ({ signal }) => searchDashboard(trimmed, signal),
    enabled: trimmed.length >= SEARCH_MIN_LENGTH,
    placeholderData: keepPreviousData,
    staleTime: 30 * 1000,
  });
};

export const useRecentItems = (limit = 6) => {
  return useQuery({
    queryKey: ["dashboardRecentItems", limit],
    queryFn: () => getRecentItems(limit),
    select: (data) => data.items,
  });
};

export const useDashboardActivity = (days: number, enabled = true) => {
  return useQuery({
    queryKey: ["dashboardActivity", days],
    queryFn: () => getDashboardActivity(days),
    placeholderData: keepPreviousData,
    enabled,
  });
};
